/**
 * Раздел «Смена» портала — этап P5 (§2, §3 ТЗ портала).
 *
 * Пять вкладок: «Чек-листы» (просмотр — проходят их в операционном боте),
 * «Задачи» (доска и карточка: взять, закрыть, отменить), «Табель» (кто на
 * смене, приход и уход, часы за период), «Шаблоны» (список и редактор) и
 * «Дисциплина» (балл смены, кто не выполнил, K1–K6). Какие вкладки видны,
 * говорит сервер (`/api/ops/shift/access`) по правам D181; он же отказывает
 * на каждом адресе, если права нет.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Denied, ErrorState, Loading } from '@shell/states';
import type { Navigation } from '../../api';
import { canSee } from '../../nav';
import { navigate, useRoute } from '../../router';
import { Checklists } from './Checklists';
import { Discipline } from './Discipline';
import { Tasks } from './Tasks';
import { Templates } from './Templates';
import { Timesheet } from './Timesheet';
import { shiftApi } from './api';
import type { ShiftAccess } from './api';
import { SHIFT_TABS, readShift, tabPath } from './paths';
import type { ShiftTab } from './paths';
import './shift.css';

const TITLES: Record<ShiftTab, string> = {
  checklists: 'Чек-листы',
  tasks: 'Задачи',
  timesheet: 'Табель',
  templates: 'Шаблоны',
  discipline: 'Дисциплина',
};

export function visibleTabs(access: ShiftAccess): ShiftTab[] {
  return SHIFT_TABS.filter((tab) => {
    switch (tab) {
      case 'checklists':
        return access.checklists;
      case 'tasks':
        return access.tasks;
      case 'timesheet':
        return access.timesheet;
      case 'templates':
        return access.templates;
      case 'discipline':
        return access.discipline;
    }
  });
}

export const accessKey = ['shift', 'access'] as const;

export default function Shift({ nav }: { nav: Navigation }) {
  const route = useRoute();
  const allowed = canSee('shift', nav);
  const access = useQuery<ShiftAccess, ApiError>({
    queryKey: accessKey,
    queryFn: () => shiftApi.access(),
    enabled: allowed,
  });

  if (!allowed) return <Denied />;
  if (access.isPending) return <Loading />;
  if (access.error) return <ErrorState error={access.error} onRetry={() => access.refetch()} />;

  const tabs = visibleTabs(access.data);
  const { tab: asked, id } = readShift(route.path);
  const tab = asked && tabs.includes(asked) ? asked : tabs[0];

  return (
    <div className="page shift-page">
      <header className="page__head shift-page__head">
        <h1 className="page__title">Смена</h1>
      </header>
      <div className="chips shift-tabs" role="tablist" aria-label="Смена">
        {tabs.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={item === tab}
            className={`chip${item === tab ? ' chip--active' : ''}`}
            onClick={() => navigate(tabPath(item))}
          >
            {TITLES[item]}
          </button>
        ))}
      </div>
      {asked && !tabs.includes(asked) ? (
        <ErrorState
          title="Эта вкладка вам не открыта"
          text="Права на разделы выдаёт владелец в «Команде»."
        />
      ) : tab === 'checklists' ? (
        <Checklists runId={id} access={access.data} />
      ) : tab === 'tasks' ? (
        <Tasks taskId={id} />
      ) : tab === 'timesheet' ? (
        <Timesheet access={access.data} />
      ) : tab === 'templates' ? (
        <Templates templateId={id} />
      ) : (
        <Discipline />
      )}
    </div>
  );
}
